import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useUser, useAuthStore } from '../store'
import { AvatarUpload } from './AvatarUpload'
import { CASDOOR_ENDPOINT } from '../casdoor'
import { useAvatarUrl } from '../hooks/useAvatarUrl'
import { useUpdateNickname } from '../hooks/useUpdateNickname'
import { openUrl } from '@tauri-apps/plugin-opener'
import OrderHistoryModal from '../../components/Plans/OrderHistoryModal'
import styles from './UserProfileEditModal.module.css'

// ============================================================================
// Types
// ============================================================================

export interface UserProfileEditModalProps {
  onClose: () => void
}

// ============================================================================
// Sub-components
// ============================================================================

interface ModalHeaderProps {
  title: string
  onClose: () => void
}

const ModalHeader = memo<ModalHeaderProps>(function ModalHeader({
  title,
  onClose,
}) {
  return (
    <div className={styles.header}>
      <h2 className={styles.title}>{title}</h2>
      <button
        type="button"
        className={styles.closeButton}
        onClick={onClose}
        aria-label="关闭"
      >
        <svg
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          width="20"
          height="20"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  )
})

// ============================================================================
// Main Component
// ============================================================================

export const UserProfileEditModal = memo<UserProfileEditModalProps>(
  function UserProfileEditModal({ onClose }) {
    const user = useUser()
    const contentRef = useRef<HTMLDivElement>(null)

    const [isEditingNickname, setIsEditingNickname] = useState(false)
    const [nicknameValue, setNicknameValue] = useState('')
    const [nicknameMsg, setNicknameMsg] = useState('')
    const [nicknameMsgType, setNicknameMsgType] = useState<'success' | 'error'>('success')
    const [ordersOpen, setOrdersOpen] = useState(false)

    // ★ PIPL 合规：注销账号（《个人信息保护法》第47条）——位于"我的帐号"最下方
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [deleteConfirmText, setDeleteConfirmText] = useState('')
    const [isDeleting, setIsDeleting] = useState(false)
    const [deleteError, setDeleteError] = useState('')
    // ★ 移动端键盘遮挡修复（二）：fixed 弹窗在移动端键盘弹出时布局视口不缩小（dvh/fixed 均不变），
    //   居中卡片的下半部被键盘盖住且无滚动空间——scrollIntoView 实测无效（上轮方案不够）。
    //   本轮改为聚焦时将卡片整体上移至顶部区域（键盘只遮挡屏幕下半部），重新打开弹窗时复位居中。
    //   仅触屏设备上移（pointer: coarse）；桌面端键盘不遮挡，保持居中。
    const [confirmKeyboardUp, setConfirmKeyboardUp] = useState(false)
    const handleDeleteConfirmInputFocus = useCallback(() => {
      if (typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches) {
        setConfirmKeyboardUp(true)
      }
    }, [])

    const handleDeleteAccount = async () => {
      if (deleteConfirmText !== '注销' || isDeleting) return
      setIsDeleting(true)
      setDeleteError('')
      try {
        const token = useAuthStore.getState().accessToken
        const apiBase = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:4000'
        const resp = await fetch(`${apiBase}/api/auth/account`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token || ''}` },
        })
        if (!resp.ok) {
          const b = await resp.json().catch(() => ({}))
          throw new Error(b?.error || `注销失败（HTTP ${resp.status}）`)
        }
        const body = await resp.json().catch(() => ({}))
        if (body?.warnings?.length) console.warn('[注销] 服务端警告:', body.warnings)
        // ★ 服务端已删除成功——后续本地清理失败不代表注销失败，
        //   各步独立容错（否则用户会误以为注销未生效而重复操作）
        try {
          await useAuthStore.getState().logout()
        } catch (e) {
          console.warn('[注销] 清理本地会话失败:', e)
        }
        // ★ 注销必须清空本地数据（云端已删；本地 SQLite 缓存残留会导致重登后旧数据还在）
        try {
          const { clearAllLocalData } = await import('@store/collections')
          await clearAllLocalData()
        } catch (e) {
          console.warn('[注销] 清空本地数据失败:', e)
        }
        onClose()
      } catch (e) {
        setDeleteError(e instanceof Error ? e.message : '注销失败，请稍后重试')
      } finally {
        setIsDeleting(false)
      }
    }

    const { avatarSrc } = useAvatarUrl(user?.name, user?.avatar)

    // 打开资料编辑页时（用户主动操作）从 Casdoor 同步最新用户信息
    // 这是 refreshUserInfo 唯一保留的触发点，降低服务器压力
    useEffect(() => {
      useAuthStore.getState().refreshUserInfo().catch(() => {})
    }, [])

    const { isUpdating, updateNickname } = useUpdateNickname({
      onSuccess: () => {
        setIsEditingNickname(false)
        setNicknameMsg('昵称更新成功')
        setNicknameMsgType('success')
        setTimeout(() => setNicknameMsg(''), 2000)
      },
      onError: (error) => {
        setNicknameMsg(error)
        setNicknameMsgType('error')
        setTimeout(() => setNicknameMsg(''), 3000)
      },
    })

    const handleEscape = useCallback(
      (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose()
        }
      },
      [onClose]
    )

    useEffect(() => {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'

      return () => {
        document.removeEventListener('keydown', handleEscape)
        document.body.style.overflow = ''
      }
    }, [handleEscape])

    useEffect(() => {
      const content = contentRef.current
      if (!content) return

      const focusableElements = content.querySelectorAll<HTMLElement>(
        'button, input, [href], select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      const handleTabKey = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault()
          lastElement?.focus()
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault()
          firstElement?.focus()
        }
      }

      content.addEventListener('keydown', handleTabKey)
      firstElement?.focus()

      return () => {
        content.removeEventListener('keydown', handleTabKey)
      }
    }, [])

    const handleBackdropClick = useCallback(
      (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
          onClose()
        }
      },
      [onClose]
    )

    const handleAvatarUploadSuccess = useCallback(
      (_avatarUrl: string) => {
        // useAvatarUpload 已处理 setUser → cache → incrementCacheKey
        // useAvatarUrl 监听 avatarCacheKey 自动刷新
      },
      []
    )

    const handleManageAccount = useCallback(() => {
      // 打开 luminote 应用的 Casdoor 登录页，确保新环境可进入登录界面
      // 使用 tauri-plugin-opener 的 openUrl：Android 用系统默认浏览器打开
      // （shell 插件的 open 对 URL 有 scope 限制，opener 专为 URL 设计且官方推荐）
      const accountUrl = `${CASDOOR_ENDPOINT}/login/luminote`
      openUrl(accountUrl).catch((err) => {
        console.error('[UserProfile] 打开账户管理页面失败:', err)
        // 兜底：WebView 内打开（至少可访问）
        window.open(accountUrl, '_blank')
      })
    }, [])

    const handleStartEditNickname = useCallback(() => {
      setNicknameValue(user?.nickname || user?.name || '')
      setNicknameMsg('')
      setIsEditingNickname(true)
    }, [user])

    const handleCancelEditNickname = useCallback(() => {
      setIsEditingNickname(false)
      setNicknameMsg('')
    }, [])

    const handleSaveNickname = useCallback(() => {
      updateNickname(nicknameValue)
    }, [updateNickname, nicknameValue])

    const handleNicknameKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
          handleSaveNickname()
        } else if (e.key === 'Escape') {
          handleCancelEditNickname()
        }
      },
      [handleSaveNickname, handleCancelEditNickname]
    )

    if (!user) {
      return null
    }

    return (
      <>
        <div
          className={styles.overlay}
        onClick={handleBackdropClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-edit-title"
      >
        <div
          ref={contentRef}
          className={styles.content}
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHeader title="我的帐号" onClose={onClose} />

          <div className={styles.form}>
            <div className={styles.avatarSection}>
              <AvatarUpload
                currentAvatar={avatarSrc}
                userName={user.nickname || user.name}
                size="lg"
                onUploadSuccess={handleAvatarUploadSuccess}
              />
            </div>

            <div className={styles.fields}>
              <div className={styles.nicknameSection}>
                {isEditingNickname ? (
                  <>
                    <label className={styles.label}>昵称</label>
                    <input
                      type="text"
                      className={`${styles.input} ${styles.nicknameInput}`}
                      value={nicknameValue}
                      onChange={(e) => setNicknameValue(e.target.value)}
                      onKeyDown={handleNicknameKeyDown}
                      placeholder="请输入新昵称"
                      maxLength={50}
                      autoFocus
                      disabled={isUpdating}
                    />
                    <div className={styles.nicknameActions}>
                      <button
                        type="button"
                        className={styles.nicknameSaveBtn}
                        onClick={handleSaveNickname}
                        disabled={isUpdating || !nicknameValue.trim()}
                      >
                        {isUpdating ? '保存中...' : '保存'}
                      </button>
                      <button
                        type="button"
                        className={styles.nicknameCancelBtn}
                        onClick={handleCancelEditNickname}
                        disabled={isUpdating}
                      >
                        取消
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={styles.nicknameDisplay}>
                      <span className={styles.label}>昵称</span>
                      <div className={styles.nicknameDisplayRow}>
                        <span className={styles.nicknameDisplayText}>
                          {user.nickname || user.name || '未设置'}
                        </span>
                        <button
                          type="button"
                          className={styles.nicknameEditBtn}
                          onClick={handleStartEditNickname}
                        >
                          <svg
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            width="14"
                            height="14"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                            />
                          </svg>
                          更改昵称
                        </button>
                      </div>
                    </div>
                  </>
                )}
                {nicknameMsg && (
                  <p className={nicknameMsgType === 'success' ? styles.nicknameSuccessMsg : styles.nicknameErrorMsg}>
                    {nicknameMsg}
                  </p>
                )}
              </div>

              <div className={styles.divider} />

              <p className={styles.infoText}>
                修改密码等其它个人信息，请前往账户管理页面
              </p>

              <div className={styles.accountActions}>
                <button
                  type="button"
                  className={styles.casdoorLink}
                  onClick={handleManageAccount}
                >
                  <svg
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                  <span>帐户管理</span>
                </button>

                <button
                  type="button"
                  className={styles.orderLink}
                  onClick={() => setOrdersOpen(true)}
                >
                  <svg
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                    />
                  </svg>
                  <span>我的订单</span>
                </button>
              </div>
            </div>

            {/* ★ 危险区域：注销账号（《个人信息保护法》第47条）——"我的帐号"最下方 */}
            <div style={{
              marginTop: 16, padding: '14px 12px', borderRadius: 10,
              border: '1px solid #fecaca', background: '#fef2f2',
            }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#dc2626', marginBottom: 6 }}>危险区域</div>
              <p style={{ fontSize: 12, color: '#b91c1c', marginBottom: 10, lineHeight: 1.5 }}>
                注销账号将永久删除您的全部数据（笔记、附件、历史版本、订阅、分享内容），并删除登录账号，不可恢复。本机应用内数据将随注销自动清除；其他已登录设备上的本地缓存将在其下次登录时自动清除。之后原邮箱可重新注册使用。请先导出数据备份。
              </p>
              <button
                type="button"
                onClick={() => { setDeleteConfirmText(''); setDeleteError(''); setConfirmKeyboardUp(false); setDeleteConfirmOpen(true) }}
                style={{
                  padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
                  background: '#dc2626', color: '#fff', border: 'none', cursor: 'pointer',
                }}
              >
                注销账号
              </button>
            </div>

            {/* 注销确认弹窗 */}
            {deleteConfirmOpen && (
              <div style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999,
                // ★ 键盘遮挡修复：overlay 可滚动（卡片超高时可滚）+ 卡片聚焦时上移顶部
                display: 'flex', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: 20,
              }} onClick={() => !isDeleting && setDeleteConfirmOpen(false)}>
                <div style={{
                  background: '#fff', borderRadius: 14, padding: 20, maxWidth: 360, width: '100%',
                  // 聚焦输入时上移：margin 顶部贴 overlay padding（20px），键盘只遮挡下半屏
                  margin: confirmKeyboardUp ? '0 auto 48px' : 'auto',
                  maxHeight: 'calc(100dvh - 48px)', overflowY: 'auto',
                }} onClick={(e) => e.stopPropagation()}>
                  <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 10, color: '#111' }}>注销账号</div>
                  <p style={{ fontSize: 13, color: '#555', marginBottom: 12, lineHeight: 1.6 }}>
                    此操作将<b style={{ color: '#dc2626' }}>永久删除</b>您的账号及全部数据（笔记、附件、历史版本、订阅、分享内容），并<b style={{ color: '#dc2626' }}>删除登录账号</b>，所有数据<b style={{ color: '#dc2626' }}>不可恢复</b>。本机应用内数据将随注销自动清除；其他已登录设备上的本地缓存将在其下次登录时自动清除。之后原邮箱可重新注册使用。请先导出数据备份。请输入 <b style={{ color: '#dc2626' }}>注销</b> 以确认：
                  </p>
                  <input
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    onFocus={handleDeleteConfirmInputFocus}
                    disabled={isDeleting}
                    placeholder="注销"
                    style={{
                      width: '100%', padding: '10px 12px', borderRadius: 8, marginBottom: 10,
                      border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box',
                    }}
                  />
                  {deleteError && (
                    <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 10 }}>{deleteError}</p>
                  )}
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => setDeleteConfirmOpen(false)}
                      disabled={isDeleting}
                      style={{
                        padding: '8px 14px', borderRadius: 8, fontSize: 13,
                        background: '#e5e7eb', color: '#333', border: 'none', cursor: 'pointer',
                      }}
                    >
                      取消
                    </button>
                    <button
                      type="button"
                      onClick={handleDeleteAccount}
                      disabled={deleteConfirmText !== '注销' || isDeleting}
                      style={{
                        padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
                        background: '#dc2626', color: '#fff', border: 'none', cursor: 'pointer',
                        opacity: deleteConfirmText !== '注销' || isDeleting ? 0.5 : 1,
                      }}
                    >
                      {isDeleting ? '注销中...' : '确认注销'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className={styles.actions}>
              <button
                type="button"
                onClick={onClose}
                className={`${styles.button} ${styles['button--primary']}`}
              >
                关闭
              </button>
            </div>
          </div>
        </div>
        </div>

        <OrderHistoryModal open={ordersOpen} onClose={() => setOrdersOpen(false)} />
      </>
    )
  }
)

export default UserProfileEditModal
